import { ISearchedAddress } from '@checkout/items';
import { IFieldsData, ILocation } from '@checkout/state';
import { Field, FormikContext } from 'formik';
import * as React from 'react';
import { ChangeEvent, FocusEvent, Fragment } from 'react';
import PlacesAutocomplete, {
  geocodeByPlaceId,
} from 'react-places-autocomplete';
import { CurrentLocationButton, InputField, PlacesAutoCompleteDropdown } from '../../presentational';

interface IOwnProps {
  formik: FormikContext<Partial<IFieldsData>>;
  location: ILocation;
  onSelect: (searchedAddress: ISearchedAddress) => void;
  onChange: (event: ChangeEvent<any>) => void;
  id: string;
  readOnly?: boolean;
  name: string;
  label: string;
  placeholder: string;
  currentLocationLabel: string;
  fetchingCurrentLocationLabel: string;
  currentLocationDenitedLabel: string;
}
interface IConnectedProps {}
interface IConnectetDispatchProps {}
interface IOwnState {
  isCurrentLocationVisible: boolean;
  isFetchingCurrentLocation: boolean;
  isCurrentLocationDenited: boolean;
  address: string;
  name: string;
}

type Props = IOwnProps & IConnectedProps & IConnectetDispatchProps;

class PlacesAutoComplete extends React.Component<Props, IOwnState> {

  state: IOwnState = {
    isCurrentLocationVisible: false,
    isFetchingCurrentLocation: false,
    isCurrentLocationDenited: false,
    address: this.props.formik.values.shippingAddress && this.props.formik.values.shippingAddress.address1 || '',
    name: 'shippingAddress.address1',
  };

  prepareAddress = (results: google.maps.GeocoderResult[]) => {
    const result = results[0];
    const adressComponents = result.address_components;

    return new Promise((resolve, reject) => {
      let searchedAddress: ISearchedAddress = {
        zipCode: '',
        cityName: '',
        street: '',
        streetNumber: '',
      };

      try {
        const zipCode = adressComponents.filter((component) => {
          return component.types.some((type: string) => type === 'postal_code');
        });

        const cityName = adressComponents.filter((component) => {
          return component.types.some((type: string) => type === 'postal_town') ||
            component.types.some((type: string) => type === 'locality');
        });

        const street = adressComponents.filter((component) => {
          return component.types.some((type: string) => type === 'route');
        });

        const streetNumber = adressComponents.filter((component) => {
          return component.types.some((type: string) => type === 'street_number');
        });

        searchedAddress = {
          zipCode: zipCode && zipCode.length > 0 ? zipCode[0].short_name : '',
          cityName: cityName && cityName.length > 0 ? cityName[0].short_name : '',
          street: street && street.length > 0 ? street[0].long_name : '',
          streetNumber: streetNumber && streetNumber.length > 0 ? streetNumber[0].short_name : '',
        };

        resolve(searchedAddress);
      } catch (e) {
        reject(e);
      }
    });
  }

  handleChange = (address: string) => {
    this.setState({
      address,
      isCurrentLocationVisible: address === '',
    });
  }

  handleSelect = (address: string, placeId: string) => {
    geocodeByPlaceId(placeId)
    .then(results => this.prepareAddress(results))
      .then((searchedAddress: ISearchedAddress) => {
        this.setState({ address: `${searchedAddress.street} ${searchedAddress.streetNumber}` });
        // Call passed callback function
        this.props.onSelect(searchedAddress);
      })
      // tslint:disable-next-line:no-console
      .catch(error => console.error('Error', error));
  }

  handleFocus = (event: FocusEvent<any>) => {
    const isCurrentLocationVisible = event.target.value === '';

    this.setState({ isCurrentLocationVisible });
  }

  handleBlur = (event: any) => {
    this.props.onChange(event);
    setTimeout(() => {
      if (!this.state.isFetchingCurrentLocation && this.state.isCurrentLocationVisible) {
        this.setState({ isCurrentLocationVisible: false });
      }
    }, 1000);
  }

  // Reverse Geocoding (Address Lookup)
  geocodeByLatLng = (lat: number, lng: number): Promise<google.maps.GeocoderResult[]> => {
    const geocoder = new window.google.maps.Geocoder();
    const OK = window.google.maps.GeocoderStatus.OK;

    return new Promise((resolve, reject) => {
      geocoder.geocode({
        location: { lat, lng }
      }, (results: google.maps.GeocoderResult[] | PromiseLike<google.maps.GeocoderResult[]> | undefined, status: string) => {
        if (status !== OK) {
          reject(status);
        }
        resolve(results);
      });
    });
  }

  handleCurrentLocationClick = () => {
    if (navigator.geolocation) {
      this.setState({ isFetchingCurrentLocation: true });

      navigator.geolocation.getCurrentPosition((location) => {

        this.geocodeByLatLng(location.coords.latitude, location.coords.longitude)
          .then(results => this.prepareAddress(results))
            .then((searchedAddress: ISearchedAddress) => {
              this.setState({
                address: `${searchedAddress.street} ${searchedAddress.streetNumber}`,
                isCurrentLocationVisible: false,
                isCurrentLocationDenited: false,
                isFetchingCurrentLocation: false,
              });
              // Call passed callback function
              this.props.onSelect(searchedAddress);
            })
            // tslint:disable-next-line:no-console
            .catch(error => console.error('Error', error));
      }, (error) => {
        if (error.code === error.PERMISSION_DENIED) {
          this.setState({
            isCurrentLocationDenited: true,
            isFetchingCurrentLocation: false,
          });
          // close current location button
          setTimeout(() => {
            if (!this.state.isFetchingCurrentLocation && this.state.isCurrentLocationVisible) {
              this.setState({ isCurrentLocationVisible: false });
            }
          }, 1000);
        }
      });
    }

  }

  render() {
    const { id, name, label, placeholder, currentLocationLabel, fetchingCurrentLocationLabel, currentLocationDenitedLabel, readOnly } = this.props;
    const { isCurrentLocationVisible, isFetchingCurrentLocation, isCurrentLocationDenited } = this.state;

    const searchOptions = {
      // radius: 3000,
      // location: { lat: 51.1126723, lng: 17.0241948 },
      componentRestrictions: {
         // country: this.props.location && this.props.location.country,
       },
    };

    return (
      <Fragment>
        <PlacesAutocomplete
          shouldFetchSuggestions={true}
          searchOptions={searchOptions}
          debounce={400}
          value={this.state.address}
          onChange={this.handleChange}
          onSelect={this.handleSelect}
        >
          {({ getInputProps, suggestions, getSuggestionItemProps, loading }) => {
            const autocompleteDropdown =
              <PlacesAutoCompleteDropdown loading={loading} suggestions={suggestions} getSuggestionItemProps={getSuggestionItemProps} />;
            const currentLocationButton =
              <CurrentLocationButton
                isVisible={isCurrentLocationVisible}
                isLoading={isFetchingCurrentLocation}
                label={currentLocationLabel}
                loadingLabel={fetchingCurrentLocationLabel}
                denitedLabel={currentLocationDenitedLabel}
                isDenited={isCurrentLocationDenited}
                onClick={this.handleCurrentLocationClick}
              />;

            return (
              <Fragment>
                <Field
                  component={InputField}
                  autocompleteDropdown={autocompleteDropdown}
                  currentLocationButton={currentLocationButton}
                  readOnly={readOnly}
                  type="text"
                  name={name}
                  id={id}
                  sizeVariant="medium"
                  label={label}
                  onFocus={this.handleFocus}
                  {...getInputProps({
                    placeholder: placeholder,
                    onBlur: (event: any) => this.handleBlur(event),
                  })}
                />
              </Fragment>
            );
          }}
        </PlacesAutocomplete>
      </Fragment>
    );
  }
}

export default PlacesAutoComplete;



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/components/container/PlacesAutoComplete/PlacesAutoComplete.tsx